import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import {
  seoGeoFields,
  systemFields,
  zReasoning,
  zTransliterations,
} from "@/lib/zod/show-fragments";

export const VenueTypeSchema = z.enum([
  "Amphitheatre",
  "Amusement Park",
  "Aquarium",
  "Arena",
  "Art Gallery",
  "Art Studio",
  "Auditorium",
  "Banquet Hall",
  "Bar",
  "Bar and Grill",
  "Beach",
  "Botanical Garden",
  "Bowling Alley",
  "Cafe",
  "Campground",
  "Casino",
  "Church",
  "City Hall",
  "Coffee Shop",
  "Comedy Club",
  "Community Center",
  "Concert Hall",
  "Convention Center",
  "Corporate Office",
  "Cultural Center",
  "Dance Hall",
  "Event Venue",
  "Farm",
  "Fine Dining Restaurant",
  "Garden",
  "Historical Landmark",
  "Hotel",
  "Inn",
  "Karaoke",
  "Library",
  "Museum",
  "Night Club",
  "Opera House",
  "Park",
  "Performing Arts Theater",
  "Philharmonic Hall",
  "Plaza",
  "Pub",
  "Ranch",
  "Resort Hotel",
  "Restaurant",
  "School",
  "Shopping Mall",
  "Stadium",
  "Tourist Attraction",
  "University",
  "Wedding Venue",
  "Wine Bar",
  "Yoga Studio",
]);

export const HiddenVenueTypeSchema = z.enum([
  "MonolithArena",
  "ConventionExpo",
  "EventHallBallroom",
  "LiveMusicStage",
  "PerformanceTheater",
  "BouzoukiaStage",
  "NightclubCommercial",
  "NightclubUnderground",
  "NightclubIndustrial",
  "NightclubAdult",
  "BarCocktailDestination",
  "BarCasualSpot",
  "BarListeningLounge",
  "AllDayHub",
  "SummerGardenTerrace",
  "RestaurantLively",
  "RivieraSummerHub",
  "HotelSocial",
  "ActivityPlayground",
  "RetailCulture",
  "DiyFreeSpace",
  "ReligiousHeritage",
  "MovingFloatingVenue",
  "PublicSpace",
  "LateNightEatery",
  "ServicePoint",
]);

const GoogleMapsPlaceSchema = z.object({
  reviews: z.array(
    z.object({
      name: z.string(),
      rating: z.number(),
      text: z.object({
        text: z.string(),
        languageCode: z.string(),
      }),
      publishTime: z.string().datetime(),
    }),
  ),
  photos: z.array(
    z.object({
      name: z.string(),
      widthPx: z.number(),
      heightPx: z.number(),
      googleMapsUri: z.string(),
    }),
  ),
});

const BraveSearchResultSchema = z.object({
  results: z.array(
    z.object({
      title: z.string(),
      url: z.string(),
      description: z.string(),
      extraSnippets: z.array(z.string()).nullish(),
    }),
  ),
});

const InstagramDataSchema = z.object({
  screenshotUrl: z.string().url(),
  username: z.string(),
});

const EnrichmentSourceSchema = z.object({
  source: z.string(),
  url: z.string().url(),
  data: z.union([
    GoogleMapsPlaceSchema,
    BraveSearchResultSchema,
    InstagramDataSchema,
    z.any(),
  ]),
});

/**
 * SUBMIT schema — what `PUT /venues/enrich` accepts.
 *
 * `.passthrough()` is load-bearing: Approve & Save PUTs this object back and
 * the endpoint **full-replaces** the stored enrichment, so a key Zod strips is
 * destroyed permanently. `taskName` — the Tina job pointer the entity trace is
 * rendered from — was being wiped on every approval. Enumerating the
 * pipeline-owned keys would re-break on the next one the flow adds.
 */
export const venueEnrichmentSubmitSchema = z
  .object({
    id: z.string(),
    reasoning: z.object({
      text: z.string(),
      confidence: z.number().min(0).max(1),
    }),
    about: z.string().superRefine(
      fieldConfig({
        fieldType: "textarea",
      }),
    ),
    // SEO/GEO contract (shared across entities).
    normalizedTitle: z.string().nullish(),
    // Full <title>. Null means "use the app's title formula".
    seoTitle: z.string().nullish(),
    shortDescription: z
      .string()
      .nullish()
      .superRefine(fieldConfig({ fieldType: "textarea" })),
    keywords: z.array(z.string()).nullish(),
    relatedSearches: z.array(z.string()).nullish(),
    faq: z
      .array(z.object({ question: z.string(), answer: z.string() }))
      .nullish(),
    area: z.string().nullish(),
    aliases: z.array(z.string()).nullish(),
    type: VenueTypeSchema,
    hiddenType: HiddenVenueTypeSchema,
    transliterations: zTransliterations,
    features: z.object({
      capacity: z.coerce.number().min(0).max(1).nullish(),
      outdoor: z.coerce.number().min(0).max(1).nullish(),
      nature: z.coerce.number().min(0).max(1).nullish(),
      view: z.coerce.number().min(0).max(1).nullish(),
      seating: z.coerce.number().min(0).max(1).nullish(),
      energy: z.coerce.number().min(0).max(1).nullish(),
      rawness: z.coerce.number().min(0).max(1).nullish(),
      undergroundness: z.coerce.number().min(0).max(1).nullish(),
      dining: z.coerce.number().min(0).max(1).nullish(),
      selectivity: z.coerce.number().min(0).max(1).nullish(),
      age: z.coerce.number().min(0).max(1).nullish(),
      queerness: z.coerce.number().min(0).max(1).nullish(),
    }),
    tags: z.array(z.string()).nullish(),
    photos: z.array(z.string().url()).nullish(),
    enrichmentSources: z.array(EnrichmentSourceSchema).nullish(),
    updatedAt: z.string().datetime({ offset: true }),
    updatedBy: z.string(),
    createdAt: z.string().datetime({ offset: true }),
    createdBy: z.string(),
  })
  .passthrough();

/**
 * READ schema — lenient, for rendering whatever the API returned.
 *
 * Every field nullish and `.passthrough()` throughout. `venueViewSchema` nests
 * this and `use-venue-repository`'s `get()` used to hard-`.parse()` the result
 * with the strict schema, so a venue whose enrichment lacked `reasoning` — or
 * carried a `type` outside the enum — blanked the entire venue edit page.
 * Consume through `parseRead()` from `@/lib/zod/read-schema`.
 */
export const venueEnrichmentReadSchema = z
  .object({
    id: z.string().nullish(),
    reasoning: zReasoning.nullish(),
    about: z.string().nullish(),
    ...seoGeoFields,
    area: z.string().nullish(),
    aliases: z.array(z.string()).nullish(),
    // Plain strings, not the enums: a venue type the backend grows before IMS
    // redeploys must not reject the payload.
    type: z.string().nullish(),
    hiddenType: z.string().nullish(),
    transliterations: zTransliterations.nullish(),
    features: z
      .object({
        capacity: z.number().nullish(),
        outdoor: z.number().nullish(),
        nature: z.number().nullish(),
        view: z.number().nullish(),
        seating: z.number().nullish(),
        energy: z.number().nullish(),
        rawness: z.number().nullish(),
        undergroundness: z.number().nullish(),
        dining: z.number().nullish(),
        selectivity: z.number().nullish(),
        age: z.number().nullish(),
        queerness: z.number().nullish(),
      })
      .passthrough()
      .nullish(),
    tags: z.array(z.string()).nullish(),
    photos: z.array(z.string()).nullish(),
    enrichmentSources: z.array(z.unknown()).nullish(),
    ...systemFields,
  })
  .passthrough();

export const venueEditableEnrichmentSchema = venueEnrichmentSubmitSchema.omit({
  id: true,
  reasoning: true,
  enrichmentSources: true,
  updatedAt: true,
  updatedBy: true,
  createdAt: true,
  createdBy: true,
});

export type GoogleMapsPlaceData = z.infer<typeof GoogleMapsPlaceSchema>;
export type InstagramData = z.infer<typeof InstagramDataSchema>;
export type BraveSearchData = z.infer<typeof BraveSearchResultSchema>;
export type EnrichmentSource = z.infer<typeof EnrichmentSourceSchema>;

export type VenueFeatures = z.infer<
  typeof venueEnrichmentSubmitSchema.shape.features
>;
export type VenueEnrichment = z.infer<typeof venueEnrichmentSubmitSchema>;
export type VenueEnrichmentRead = z.infer<typeof venueEnrichmentReadSchema>;
/** @deprecated Prefer the explicit read/submit pair. Kept so existing importers compile. */
export { venueEnrichmentSubmitSchema as venueAIEnrichmentSchema };
